/** Reference Data (Demo Mode only, Range Planning) - home for supporting configuration that
 * underpins range development (Item Master, Inventory Categories, Units of Measure) without
 * exposing each as an equal top-level Ranges journey. Each card links to its existing page and
 * is gated by that page's own permission - see .github/instructions/sscm-area.instructions.md. */

import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Grid, SvgIcon, Typography } from '@mui/material';
import { FastfoodOutlined, ScaleOutlined } from '@mui/icons-material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { UserContext } from '../../components/shared/useUser';
import { Grid01 } from '../../assets';

const ReferenceDataPage = () => {
  const { t } = useTranslation('referenceData');
  const navigate = useNavigate();
  const { hasPermissionTo } = useContext(UserContext);

  const cards = [
    {
      key: 'itemMaster',
      icon: <FastfoodOutlined />,
      href: '/range-planning/items',
      visible: hasPermissionTo(['RangePlanningItemsRead']),
    },
    {
      key: 'inventoryCategories',
      icon: <SvgIcon><Grid01 /></SvgIcon>,
      href: '/manage-categories',
      visible: hasPermissionTo(['CategoryAdmin']),
    },
    {
      key: 'unitsOfMeasure',
      icon: <ScaleOutlined />,
      href: '/uom-types',
      visible: hasPermissionTo(['UomTypeView']),
    },
  ].filter((card) => card.visible);

  return (
    <PageArea>
      <PageTitle>{t('title', 'Reference Data')}</PageTitle>
      <Typography variant="textSM"
        color="text.secondary"
        sx={{ mt: 1, mb: 4, maxWidth: 640 }}>
        {t('intro', 'Supporting configuration that underpins range development - master item definitions, inventory categories and units of measure.')}
      </Typography>

      <Grid container
        spacing={4}>
        {cards.map((card) => (
          <Grid item
            key={card.key}
            xs={12}
            md={4}>
            <Section>
              <Box
                sx={{ p: 4, cursor: 'pointer' }}
                onClick={() => navigate(card.href)}
              >
                <Box sx={{ color: 'primary.main', mb: 2 }}>{card.icon}</Box>
                <Typography variant="textSM"
                  fontWeight={600}
                  sx={{ mb: 1 }}>
                  {t(`card.${card.key}.title`, card.key)}
                </Typography>
                <Typography variant="textXS"
                  color="text.secondary">
                  {t(`card.${card.key}.desc`, '')}
                </Typography>
              </Box>
            </Section>
          </Grid>
        ))}
        {cards.length === 0 && (
          <Grid item
            xs={12}>
            <Typography variant="textSM"
              color="text.secondary">
              {t('noAccess', 'You do not have access to any reference data areas.')}
            </Typography>
          </Grid>
        )}
      </Grid>
    </PageArea>
  );
};

export default ReferenceDataPage;
